import { useEffect, useState } from "react";
import { getDictionary } from "@/i18n";
import { useLanding } from "@/context/LandingContext";
import { publicApi } from "@/lib/api";
import { SEMINAR } from "@/seminars";
import { RegistrationSuccess } from "./RegistrationSuccess";
import { StepBar, StepOne, StepTwo } from "./RegistrationSteps";

const EMPTY = { full_name: "", email: "", mobile: "", question: "", invited_by: "", age_range: "", consent: false };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function RegistrationForm({ scope = "page" }) {
  const sfx = scope === "modal" ? "-modal" : "";
  const { t, lang, selectedEvent, sessionTag, questionId, setQuestionId, quickEmail, progressive, refCode, refAdviser, adSource } = useLanding();
  const r = t.register;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (quickEmail) setForm((prev) => ({ ...prev, email: quickEmail }));
  }, [quickEmail]);

  useEffect(() => {
    if (questionId && questionId !== "other") setForm((prev) => ({ ...prev, question: r.questions[questionId] }));
  }, [questionId, r]);

  // Visitors arriving through an adviser's ?ref= link get "Invited by" filled with that adviser's name and locked,
  // so attribution in the dashboard always matches the link that was used.
  const lockedInvitedBy = Boolean(refAdviser?.name);
  useEffect(() => {
    if (refAdviser?.name) setForm((prev) => ({ ...prev, invited_by: refAdviser.name }));
  }, [refAdviser]);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const togglePill = (id) => {
    const label = r.questions[id];
    const active = form.question === label;
    setQuestionId(active ? "" : id);
    set("question", active ? "" : label);
  };

  const digits = form.mobile.replace(/\s/g, "");

  const next = () => {
    if (form.full_name.trim().length < 2) return setError(r.messages.required);
    if (!/^\d{8}$/.test(digits)) return setError(r.messages.mobile);
    if (!EMAIL_RE.test(form.email.trim())) return setError(r.messages.email);
    setError("");
    setStep(2);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (step === 1) return next();
    if (!form.age_range) return setError(r.messages.ageRange);
    if (!form.consent) return setError(r.messages.consent);
    const sessionLang = selectedEvent.langCode;
    const sessionDict = getDictionary(sessionLang);
    const payload = {
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      mobile: `${r.mobilePrefix} ${digits}`,
      event_date: selectedEvent.label[sessionLang],
      event_date_iso: selectedEvent.iso,
      event_language: selectedEvent.language,
      event_time: sessionDict.register.time,
      event_venue: sessionDict.register.venue,
      main_question: form.question.trim(),
      invited_by: form.invited_by.trim(),
      age_range: getDictionary("en").register.ageRanges[form.age_range],
      consent: form.consent,
      registration_source: progressive ? "hero_progressive_form" : scope === "modal" ? "modal_form" : "full_page_form",
      ref_code: refCode.current || null,
      page_language: lang,
      ...adSource.current,
    };
    setSubmitting(true);
    try {
      const { data } = await publicApi.post("/registrations", payload);
      const share = new URL(window.location.origin);
      share.searchParams.set("lang", lang);
      if (refCode.current) share.searchParams.set("ref", refCode.current);
      share.searchParams.set("utm_source", "whatsapp");
      share.searchParams.set("utm_medium", "share");
      share.searchParams.set("utm_campaign", "seat-share");
      setSuccess({ id: data.id, calendarToken: data.calendar_token, date: selectedEvent.label[lang], tag: sessionTag(selectedEvent), shareUrl: share.toString() });
      setForm(EMPTY);
      setStep(1);
      setQuestionId("");
      if (window.fbq) {
        const meta = { content_name: SEMINAR.pixelContentName, content_category: selectedEvent.language, content_ids: [selectedEvent.iso] };
        window.fbq("track", "CompleteRegistration", { ...meta, status: true, value: 0, currency: "SGD" });
        window.fbq("track", "Lead", meta);
      }
      window.dispatchEvent(new CustomEvent("affinity:legacy-registration", { detail: payload }));
    } catch (err) {
      setError(err.response?.status === 409 ? r.messages.duplicate : r.messages.failed);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) return <RegistrationSuccess r={r} success={success} onAnother={() => setSuccess(null)} sfx={sfx} />;

  return (
    <form className="registration-card" onSubmit={onSubmit} noValidate id={`registrationForm${sfx}`} data-testid={`registration-form${sfx}`} data-step={step}>
      <StepBar step={step} labels={r.steps} sfx={sfx} />
      {step === 1 ? (
        <StepOne r={r} form={form} set={set} onNext={next} sfx={sfx} />
      ) : (
        <StepTwo r={r} form={form} set={set} onBack={() => { setError(""); setStep(1); }} submitting={submitting} togglePill={togglePill} consentError={error === r.messages.consent} ageError={error === r.messages.ageRange} sfx={sfx} lockedInvitedBy={lockedInvitedBy} />
      )}
      <div className={`form-message ${error ? "error" : ""}`} role="status" aria-live="polite" data-testid={`form-message${sfx}`}>{error}</div>
      <p className="reg-note" data-testid={`reg-privacy-note${sfx}`}>{step === 1 ? r.privacyNote : r.reassurance}</p>
    </form>
  );
}
